/**
 * agenda-card.js
 *
 * Factory de cartes d'animation pour l'agenda frontend.
 *
 * Exports :
 *   renderCard3col(anim, options?) → string HTML
 *   renderCard2col(anim, options?)  → string HTML
 *
 * Chaque fonction retourne un <article class="agenda-card"> prêt à être
 * inséré dans le DOM. Le JS (agenda.js) se charge d'attacher les
 * gestionnaires d'événements après insertion.
 *
 * Paramètre `anim` — objet animation tel que retourné par build_agenda_payload() :
 * {
 *   id, title, featured_image,
 *   categories: [{name, color}],
 *   sessions:   [{date, heure_debut, heure_fin, lieu_depart, ...}],
 *   dates:      ['Y-m-d', ...],
 *   lieu,
 *   places_disponibles,
 *   disable_native_inscription,
 *   external_inscription_url,
 *   external_inscription_label,
 * }
 */

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------

/** Échappe les caractères HTML dangereux dans une chaîne. */
function esc(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Formate une date Y-m-d en "dim. 12 mars" (fr). */
function formatDateShort(ymd) {
    if (!ymd) return '';
    const d = new Date(ymd + 'T00:00:00');
    if (isNaN(d)) return ymd;
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
}

/** Formate l'heure "HH:MM:SS" → "HH:MM". */
function formatTime(t) {
    if (!t) return '';
    return String(t).slice(0, 5);
}

/**
 * Construit le bloc de badges de dates d'une animation.
 * - Sessions multi-jours (date_fin > date) → "Du X au Y"
 * - Sessions sur une journée → "X mai · 09:00–17:00"
 * - Sans sessions : affichage brut des dates.
 * On affiche au maximum `max` sessions puis un indicateur "+N".
 */
function buildDateBadges(dates, sessions, max = 3) {
    if (!dates || dates.length === 0) return '';

    if (sessions && sessions.length > 0) {
        const shown = sessions.slice(0, max);
        const extra = sessions.length - shown.length;

        const badges = shown.map(s => {
            const dateFin   = s.date_fin;
            const isMultiDay = dateFin && dateFin !== s.date && dateFin > s.date;
            if (isMultiDay) {
                return `<span class="agenda-card__date-badge">Du ${esc(formatDateShort(s.date))} au ${esc(formatDateShort(dateFin))}</span>`;
            }
            const hd   = formatTime(s.heure_debut);
            const hf   = formatTime(s.heure_fin);
            const time = hd ? ` · ${hd}${hf ? `\u2013${hf}` : ''}` : '';
            return `<span class="agenda-card__date-badge">${esc(formatDateShort(s.date))}${esc(time)}</span>`;
        });

        if (extra > 0) {
            badges.push(`<span class="agenda-card__date-badge agenda-card__date-badge--more">+${extra}</span>`);
        }

        return `<div class="agenda-card__dates">${badges.join('')}</div>`;
    }

    // Fallback sans sessions : dédupliquer et trier les dates
    const unique = [...new Set(dates)].sort();
    const shown  = unique.slice(0, max);
    const extra  = unique.length - shown.length;

    const badges = shown.map(d =>
        `<span class="agenda-card__date-badge">${esc(formatDateShort(d))}</span>`
    );

    if (extra > 0) {
        badges.push(`<span class="agenda-card__date-badge agenda-card__date-badge--more">+${extra}</span>`);
    }

    return `<div class="agenda-card__dates">${badges.join('')}</div>`;
}

/** Construit les chips de catégories miniatures affichées dans la carte. */
function buildCatTags(categories) {
    if (!categories || categories.length === 0) return '';
    const tags = categories.map(cat => {
        const color = esc(cat.color || '#4ECDC4');
        return `<span class="agenda-card__cat-tag" style="--cat-color:${color};">${esc(cat.name)}</span>`;
    });
    return `<div class="agenda-card__cats">${tags.join('')}</div>`;
}

/** Construit la partie image de la carte (avec placeholder SVG si aucune image). */
function buildImageBlock(anim) {
    if (anim.featured_image) {
        return `
<div class="agenda-card__image">
    <img
        src="${esc(anim.featured_image)}"
        alt="${esc(anim.title)}"
        loading="lazy"
        decoding="async"
    >
</div>`;
    }
    // Placeholder avec initiale
    const initial = anim.title ? esc(anim.title.charAt(0).toUpperCase()) : '?';
    return `
<div class="agenda-card__image agenda-card__image--placeholder">
    <svg class="agenda-card__placeholder" width="48" height="48" viewBox="0 0 24 24"
         fill="none" stroke="#adb5bd" stroke-width="1.5" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
    </svg>
    <span class="agenda-card__initial" aria-hidden="true">${initial}</span>
</div>`;
}

// ---------------------------------------------------------------------------
// Bouton détail
// ---------------------------------------------------------------------------
function buildDetailBtn(anim) {
    return `
<button
    class="agenda-card__btn agenda-card__btn--detail"
    type="button"
    data-animation-id="${esc(String(anim.id))}"
    aria-label="Voir le détail de ${esc(anim.title)}"
>Détail de l'animation</button>`;
}

// ---------------------------------------------------------------------------
// Bouton d'inscription
// ---------------------------------------------------------------------------
function buildInscriptionBtn(anim) {
    // -1 = jauge illimitée (toujours inscriptible) ; 0 = complet ; >0 = stock géré
    const complet = anim.places_disponibles === 0;

    // L'inscription externe est prioritaire sur inscription_libre
    if (anim.disable_native_inscription && anim.external_inscription_url) {
        const label = anim.external_inscription_label || "S'inscrire";
        return `
<a
    class="agenda-card__btn agenda-card__btn--external"
    href="${esc(anim.external_inscription_url)}"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="${esc(label + ' — ' + anim.title)}"
>${esc(label)}</a>`;
    }

    if (anim.inscription_libre) {
        return `<span class="agenda-card__btn agenda-card__btn--libre" aria-disabled="true">Accès libre</span>`;
    }

    if (complet) {
        return `<button class="agenda-card__btn agenda-card__btn--complet" type="button" disabled aria-disabled="true">Complet</button>`;
    }

    return `
<button
    class="agenda-card__btn agenda-card__btn--inscrit"
    type="button"
    data-animation-id="${esc(String(anim.id))}"
    aria-label="S'inscrire à ${esc(anim.title)}"
>S'inscrire</button>`;
}

// ---------------------------------------------------------------------------
// Bloc actions (Détail + Inscription)
// ---------------------------------------------------------------------------
function buildActions(anim) {
    return `${buildDetailBtn(anim)}${buildInscriptionBtn(anim)}`;
}

// ---------------------------------------------------------------------------
// Export principal : carte 3 colonnes (Catégorie + Date)
// ---------------------------------------------------------------------------

/**
 * Génère une carte enrichie pour les grilles 3 colonnes.
 * Affiche : image · chips catégories · titre · dates+horaires · lieu · bouton.
 *
 * @param {Object} anim  — données animation
 * @returns {string}     — HTML de l'article
 */
export function renderCard3col(anim) {
    const dateBadges = buildDateBadges(anim.dates, anim.sessions, 3);
    const villes     = anim.villes && anim.villes.length ? [...new Set(anim.villes)].join(', ') : '';
    const lieu       = anim.lieu || (anim.sessions && anim.sessions[0] && anim.sessions[0].lieu_depart) || '';

    return `
<article
    class="agenda-card agenda-card--full"
    role="listitem"
    data-animation-id="${esc(String(anim.id))}"
    data-categories="${esc(JSON.stringify((anim.categories || []).map(c => c.name)))}"
    data-dates="${esc(JSON.stringify(anim.dates || []))}"
    data-lieu="${esc(lieu)}"
>
    ${buildImageBlock(anim)}

    <div class="agenda-card__body">
        ${buildCatTags(anim.categories)}

        <h3 class="agenda-card__title">${esc(anim.title)}</h3>

        <div class="agenda-card__meta">
            ${dateBadges ? `<div class="agenda-card__meta-row">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                ${dateBadges}
            </div>` : ''}

            ${villes ? `<div class="agenda-card__meta-row agenda-card__lieu">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                </svg>
                <span>${esc(villes)}</span>
            </div>` : ''}
        </div>

        <div class="agenda-card__actions">
            ${buildActions(anim)}
        </div>
    </div>
</article>`;
}

// ---------------------------------------------------------------------------
// Export : carte 2 colonnes (Lieu) — version compacte
// ---------------------------------------------------------------------------

/**
 * Génère une carte compacte pour la grille 2 colonnes de l'onglet Lieu.
 * Affiche : image · titre · date(s) — sans catégories ni bouton d'inscription.
 *
 * @param {Object} anim  — données animation
 * @returns {string}     — HTML de l'article
 */
export function renderCard2col(anim) {
    // Pour l'onglet Lieu, 1 seule date suffit (la prochaine)
    const dateBadges = buildDateBadges(anim.dates, anim.sessions, 2);
    const villes     = anim.villes && anim.villes.length ? [...new Set(anim.villes)].join(', ') : '';
    const lieu       = anim.lieu || (anim.sessions && anim.sessions[0] && anim.sessions[0].lieu_depart) || '';

    return `
<article
    class="agenda-card agenda-card--compact"
    role="listitem"
    data-animation-id="${esc(String(anim.id))}"
    data-dates="${esc(JSON.stringify(anim.dates || []))}"
    data-lieu="${esc(lieu)}"
>
    ${buildImageBlock(anim)}

    <div class="agenda-card__body">
        <h3 class="agenda-card__title">${esc(anim.title)}</h3>

        <div class="agenda-card__meta">
            ${dateBadges ? `<div class="agenda-card__meta-row">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                ${dateBadges}
            </div>` : ''}

            ${villes ? `<div class="agenda-card__meta-row agenda-card__lieu">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                </svg>
                <span>${esc(villes)}</span>
            </div>` : ''}
        </div>

        <div class="agenda-card__actions">
            ${buildActions(anim)}
        </div>
    </div>
</article>`;
}

